import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '@/lib/api';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { User } from '@phosphor-icons/react';
import { PRODUCT_UNIVERS_STYLES } from '@/lib/univers';
import { StatusDot, type StatusTone } from '@/components/ui/status-dot';

const PRODUCT_LABELS: Record<string, string> = {
  vtc: 'VTC',
  scolaire: 'Scolaire',
  sante: 'Mutuelle',
  emprunteur: 'Emprunteur',
  auto: 'Auto',
  moto: 'Moto',
  vsp: 'VSP',
  pno: 'PNO',
  animal: 'Santé animal',
  habitation: 'MRH',
  expatrie: 'Expatrié',
  prevoyance: 'Prévoyance',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  quoted: 'Tarifs obtenus',
  selected: 'Formule choisie',
  under_review: 'En vérification',
  transmitted: 'Transmis',
  signing: 'En signature',
  signed: 'Signé',
  submitted: 'En attente de souscription',
  awaiting_client_signature: 'En attente signature client',
  pending_docs: 'Docs en attente',
  accepted: 'Contrat actif',
  expired: 'Expiré',
  cancelled: 'Annulé',
  rejected: 'Refusé',
  perdu: 'Perdu',
};

// Ton du statut : point coloré + libellé (les couleurs vives restent aux produits).
const STATUS_TONES: Record<string, StatusTone> = {
  draft: 'neutral', quoted: 'info', selected: 'info', under_review: 'warning', transmitted: 'info',
  signing: 'info', signed: 'success', submitted: 'info', awaiting_client_signature: 'warning',
  pending_docs: 'warning', accepted: 'success', expired: 'neutral', cancelled: 'danger', rejected: 'danger',
  perdu: 'danger',
};

// Couleurs d'univers des produits, communes à tout le back office.
const PRODUCT_STYLES = PRODUCT_UNIVERS_STYLES;

interface SearchResult {
  id: string;
  type: 'contact' | 'devis' | 'contrat';
  title: string;
  subtitle: string;
  product_type?: string;
  status?: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  // Raccourci ⌘K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Recherche avec debounce
  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/search?q=${encodeURIComponent(q)}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch {
      // Silently fail
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    // Rediriger vers la fiche contact (le devis est lié au contact)
    navigate(`/contacts/${result.id}`);
  };

  // Filtrage fait par le serveur (téléphone sans espaces, +33…) : cmdk ne refiltre pas.
  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Contact, devis, téléphone, code postal…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {query.length < 2 && (
          <div className="py-8 text-center">
            <p className="text-sm text-ink-muted">Tapez au moins 2 caractères pour rechercher</p>
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              <span className="text-xs text-ink-muted bg-[#EEF1F6] px-2 py-1 rounded-goutte-sm">Nom / Prénom</span>
              <span className="text-xs text-ink-muted bg-[#EEF1F6] px-2 py-1 rounded-goutte-sm">Email</span>
              <span className="text-xs text-ink-muted bg-[#EEF1F6] px-2 py-1 rounded-goutte-sm">Téléphone</span>
              <span className="text-xs text-ink-muted bg-[#EEF1F6] px-2 py-1 rounded-goutte-sm">N° de devis</span>
              <span className="text-xs text-ink-muted bg-[#EEF1F6] px-2 py-1 rounded-goutte-sm">Code postal</span>
            </div>
          </div>
        )}

        {query.length >= 2 && !isSearching && results.length === 0 && (
          <CommandEmpty>Aucun résultat pour « {query} »</CommandEmpty>
        )}

        {isSearching && (
          <div className="py-6 text-center text-sm text-ink-muted">
            Recherche en cours...
          </div>
        )}

        {results.length > 0 && (
          <CommandGroup heading="Contacts">
            {results.map((result) => (
              <CommandItem
                key={result.id}
                value={`${result.title} ${result.subtitle}`}
                onSelect={() => handleSelect(result)}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-3 w-full py-1">
                  <div className="size-9 rounded-full bg-pro-tint flex items-center justify-center flex-shrink-0">
                    <User className="size-4 text-ink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{result.title}</p>
                    <p className="text-xs text-ink-muted truncate">{result.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {result.product_type && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-goutte-sm text-[10px] font-semibold border ${PRODUCT_STYLES[result.product_type] || 'bg-[#EEF1F6] text-ink-soft border-rule'}`}>
                        {PRODUCT_LABELS[result.product_type] || result.product_type}
                      </span>
                    )}
                    {result.status && (
                      <StatusDot tone={STATUS_TONES[result.status] ?? 'neutral'} className="text-[10px]">
                        {STATUS_LABELS[result.status] || result.status}
                      </StatusDot>
                    )}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
